import usePageSEO from '../hooks/usePageSEO';
import { ProductSchema } from '../components/JsonLd';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { 
  FileText, 
  ArrowRight, 
  CheckCircle2, 
  Download,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EBOOK_IMAGE = "https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/o4nj6eel_plouton-linkedin-ad%20%282%29.jpeg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_64eb10e1-fdde-4977-afc2-4761fbf5691b/artifacts/5pkoywp1_Plouton_Logo%20%281%29.png";

export const ResourcesPage = () => {
  usePageSEO({
    title: 'Finance Automation Resources & Ebooks | Plouton AI',
    description: 'Free ebooks, guides, and playbooks on AP automation, financial close, and AI agents for finance teams. Download instantly.',
    path: '/resources'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    workEmail: '',
    companySize: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCompanySizeChange = (value) => {
    setFormData(prev => ({ ...prev, companySize: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.workEmail || !formData.companySize) {
      toast.error('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.workEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          work_email: formData.workEmail,
          company_size: formData.companySize,
          source: 'ebook_download',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setIsSubmitted(true);
      toast.success('Thank you! Check your email for the download link.');
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] pt-[108px]">
      <ProductSchema
        name="Plouton AI Resources"
        description="Download ebooks, guides, and resources on AI-powered finance automation. Learn how to automate month-end close and AP."
        url="https://plouton.co/resources"
      />
      {/* Hero Section */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a0f1a] via-[#0d1424] to-[#0a0f1a]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-orange-500/5 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Left Column - Content (Brex style) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:sticky lg:top-32"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full text-sm font-medium text-orange-400 mb-6">
                <FileText className="w-4 h-4" />
                <span>Free Report</span>
              </span>

              <h1 
                className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6 leading-[1.1]"
                data-testid="resources-headline"
              >
                A practical guide to{' '}
                <span className="text-orange-500">AI agents</span> in finance operations.
              </h1>

              <h2 className="text-xl text-slate-300 mb-6">
                See how AI can amplify your effort.
              </h2>

              <p className="text-slate-400 leading-relaxed mb-8">
                What if you had a tireless co-worker who could handle your manual tasks, follow your processes, and offer real-time assistance? That's the power of AI agents—and the promise of intelligent finance operations.
              </p>

              <p className="text-white font-medium mb-4">
                Read the full guide to learn:
              </p>

              <ul className="space-y-3 text-slate-400">
                <li className="flex items-start gap-3">
                  <span className="text-slate-600 mt-1">—</span>
                  <span>How finance leaders should be approaching (and championing) the use of AI.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-slate-600 mt-1">—</span>
                  <span>Ways that AI is empowering finance teams today to do more with less.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-slate-600 mt-1">—</span>
                  <span>What's in store for the future of AI agents in finance.</span>
                </li>
              </ul>
            </motion.div>

            {/* Right Column - Image + Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              {/* Ebook Image Preview */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={EBOOK_IMAGE}
                  alt="AI Agents for Finance Teams - Free Ebook on Month-End Close Automation"
                  className="w-full h-auto"
                  width={600}
                  height={600}
                  loading="lazy"
                  data-testid="ebook-preview-image"
                />
                {/* Floating badge */}
                <div className="absolute top-4 right-4 bg-orange-500 text-white px-4 py-2 rounded-full font-semibold text-sm shadow-lg">
                  Free Download
                </div>
              </div>

              {/* Form Card */}
              <div className="bg-[#111827] border border-slate-800/50 rounded-2xl p-6 sm:p-8">
                {!isSubmitted ? (
                  <>
                    <h2 className="font-heading text-xl font-bold text-white mb-2">
                      Get Your Free Copy
                    </h2>
                    <p className="text-slate-400 text-sm mb-6">
                      Enter your info to download the full report instantly.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-capture-form">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="firstName" className="text-slate-300 font-medium text-sm">
                            First name
                          </Label>
                          <Input
                            id="firstName"
                            name="firstName"
                            type="text"
                            placeholder="John"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className="mt-1.5 bg-slate-900/50 border-slate-700/50 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                            data-testid="input-first-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="lastName" className="text-slate-300 font-medium text-sm">
                            Last name
                          </Label>
                          <Input
                            id="lastName"
                            name="lastName"
                            type="text"
                            placeholder="Smith"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            className="mt-1.5 bg-slate-900/50 border-slate-700/50 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                            data-testid="input-last-name"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="workEmail" className="text-slate-300 font-medium text-sm">
                          Work email
                        </Label>
                        <Input
                          id="workEmail"
                          name="workEmail"
                          type="email"
                          placeholder="john@company.com"
                          value={formData.workEmail}
                          onChange={handleInputChange}
                          className="mt-1.5 bg-slate-900/50 border-slate-700/50 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                          data-testid="input-work-email"
                        />
                      </div>

                      <div>
                        <Label htmlFor="companySize" className="text-slate-300 font-medium text-sm">
                          Company size
                        </Label>
                        <Select 
                          value={formData.companySize} 
                          onValueChange={handleCompanySizeChange}
                        >
                          <SelectTrigger 
                            className="mt-1.5 bg-slate-900/50 border-slate-700/50 text-white focus:border-blue-500 focus:ring-blue-500/20" 
                            data-testid="select-company-size"
                          >
                            <SelectValue placeholder="Select company size" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="1-10" className="text-white focus:bg-slate-700">1-10 employees</SelectItem>
                            <SelectItem value="11-50" className="text-white focus:bg-slate-700">11-50 employees</SelectItem>
                            <SelectItem value="51-200" className="text-white focus:bg-slate-700">51-200 employees</SelectItem>
                            <SelectItem value="201-500" className="text-white focus:bg-slate-700">201-500 employees</SelectItem>
                            <SelectItem value="501-1000" className="text-white focus:bg-slate-700">501-1000 employees</SelectItem>
                            <SelectItem value="1000+" className="text-white focus:bg-slate-700">1000+ employees</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-orange-500 text-white hover:bg-orange-600 transition-all duration-300 font-semibold rounded-lg py-5 mt-2"
                        data-testid="lead-capture-form-submit"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Download Now
                            <Download className="w-5 h-5 ml-2" />
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-slate-500 text-center">
                        By submitting, you agree to receive marketing communications from Plouton AI.
                      </p>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-6" data-testid="form-success-message">
                    <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-white mb-2">
                      Thank you!
                    </h2>
                    <p className="text-slate-400 mb-6">
                      We've sent the download link to your email. Check your inbox for the report.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setIsSubmitted(false)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    >
                      Submit another request
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Resources Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4 block">
              More Resources
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Continue Learning
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/guides/test-guide">
              <motion.div
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:border-slate-300 transition-all duration-300 cursor-pointer group h-full"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 bg-blue-100 text-blue-600">
                  Guide
                </span>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                  Getting Started with AI Agents
                </h3>
                <p className="text-slate-600 text-sm mb-4">A comprehensive guide to understanding and implementing AI agents in your finance workflows.</p>
                <span className="text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all text-blue-600">
                  Read guide <ArrowRight className="w-4 h-4" />
                </span>
              </motion.div>
            </Link>

            <Link to="/use-cases/accounts-payable-automation">
              <motion.div
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:border-slate-300 transition-all duration-300 cursor-pointer group h-full"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 bg-orange-100 text-orange-600">
                  Use Case
                </span>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                  Accounts Payable Automation
                </h3>
                <p className="text-slate-600 text-sm mb-4">Transform your AP process from a manual bottleneck into streamlined, accurate operations.</p>
                <span className="text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all text-orange-500">
                  Learn more <ArrowRight className="w-4 h-4" />
                </span>
              </motion.div>
            </Link>

            <Link to="/comparisons/plouton-vs-traditional-rpa">
              <motion.div
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:border-slate-300 transition-all duration-300 cursor-pointer group h-full"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 bg-purple-100 text-purple-600">
                  Comparison
                </span>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                  Plouton vs Traditional RPA
                </h3>
                <p className="text-slate-600 text-sm mb-4">See why AI-powered browser agents are replacing traditional RPA for finance automation.</p>
                <span className="text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all text-purple-600">
                  Compare <ArrowRight className="w-4 h-4" />
                </span>
              </motion.div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
            Ready to see Plouton AI in action?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Book a demo to see how we can automate your finance workflows.
          </p>
          <a 
            href="https://cal.com/muhammad-furqan-karim-kidwai-uvsohz/30min" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button 
              size="lg"
              className="bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg transition-all duration-300 font-semibold rounded-lg px-10 py-6 text-lg"
              data-testid="cta-book-demo-button"
            >
              Book a Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResourcesPage;
